import { useCallback, useState } from 'react';

/**
 * "I have seen this, stop showing it."
 *
 * Both things this backs — the notification nudge and the install prompt — are suggestions
 * about a setting that is permanently reachable elsewhere. Shown once they are helpful;
 * shown every visit they are an argument the reader cannot win, and the usual response is
 * to stop reading anything the app puts in that spot.
 *
 * Kept in `localStorage` rather than on the account on purpose. Both prompts are about the
 * *device* in front of you — whether this browser is installed, whether this browser has
 * notifications — so a dismissal on a laptop should say nothing about a phone.
 *
 * Expiring rather than permanent, because the answer can change: someone who declined to
 * install in September may well want to in November, and a nudge that never returns is
 * indistinguishable from a feature that does not exist.
 */

const PREFIX = 'pickem.dismissed.';

/** Every read and write is guarded: private windows and blocked site data both throw. */
function readExpiry(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeExpiry(key: string, at: number): void {
  try {
    window.localStorage.setItem(PREFIX + key, String(at));
  } catch {
    // Storage is unavailable. The prompt reappears next visit, which is a worse experience
    // than remembering — and a far better one than failing to render the page.
  }
}

export interface Dismissible {
  isDismissed: boolean;
  dismiss: () => void;
}

export function useDismissed(key: string, days = 30): Dismissible {
  const [dismissedUntil, setDismissedUntil] = useState<number | null>(() => readExpiry(key));

  const dismiss = useCallback(() => {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    writeExpiry(key, until);
    setDismissedUntil(until);
  }, [key, days]);

  return {
    isDismissed: dismissedUntil !== null && dismissedUntil > Date.now(),
    dismiss,
  };
}
